import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrainFront, CarFront, Plane, TrainTrack, X } from 'lucide-react';
import { CONNECTIVITY } from '@/content';
import ZoomImage from '@/components/ZoomImage';
import { track } from '@/lib/track';
import RedO from '@/components/RedO';

const ICONS = { rail: TrainFront, road: CarFront, air: Plane, metro: TrainTrack };

export default function Connectivity() {
  const [layer, setLayer] = useState(CONNECTIVITY.layers[0]);

  const pick = (l) => {
    setLayer(l);
    track('map_interaction', { type: 'connectivity_layer', layer: l.id });
  };

  return (
    <section id="connectivity" data-testid="connectivity-section" className="scroll-mt-20 overflow-hidden py-20 sm:py-28" aria-labelledby="connectivity-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="eyebrow">{CONNECTIVITY.eyebrow}</p>
        <h2 id="connectivity-heading" className="headline mt-3 text-3xl sm:text-5xl">
          <RedO text={CONNECTIVITY.headline[0]} />
          <span className="mt-1 block"><RedO text={CONNECTIVITY.headline[1]} /></span>
          <span className="mt-1 block text-[#646A72]"><RedO text={CONNECTIVITY.headline[2]} /></span>
        </h2>
        <p className="mt-4 font-display text-sm font-bold uppercase tracking-[0.22em] text-[#A41225]">{CONNECTIVITY.sub}</p>

        <div className="mt-10 grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]">
          {/* tilted map */}
          <motion.div
            className="relative min-w-0"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            style={{ perspective: '1100px' }}
          >
            <motion.div
              className="relative"
              initial={false}
              whileInView={{ rotateX: 6, rotateZ: -1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <ZoomImage
                src={CONNECTIVITY.map}
                alt="Approved artistic location map of Navade showing Aarohi Complex, the rail corridor, roads towards Panvel and Navi Mumbai, Taloja MIDC and the proposed metro corridor"
                testid="map-viewer"
                aspect="2400 / 1206"
              />
              {/* layer accent overlay */}
              <AnimatePresence>
                <motion.div
                  key={layer.id}
                  className="pointer-events-none absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    background: layer.operational
                      ? 'radial-gradient(60% 50% at 55% 45%, rgba(201,47,75,0.10), transparent 70%)'
                      : 'radial-gradient(60% 50% at 55% 45%, rgba(167,171,176,0.16), transparent 70%)',
                  }}
                />
              </AnimatePresence>
              <div className="absolute left-3 top-3 rounded-full bg-white px-4 py-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-[#1C1D21] shadow-sm">
                {layer.label} · {layer.short}
              </div>
            </motion.div>
          </motion.div>

          {/* layer selector + detail */}
          <div className="min-w-0">
            <div className="grid grid-cols-2 gap-3" role="tablist" aria-label="Connectivity layers">
              {CONNECTIVITY.layers.map((l) => {
                const Icon = ICONS[l.id];
                const on = layer.id === l.id;
                return (
                  <button
                    key={l.id}
                    data-testid={`layer-${l.id}-btn`}
                    role="tab"
                    aria-selected={on}
                    onClick={() => pick(l)}
                    className={`flex min-h-[64px] flex-col items-start gap-1.5 rounded-2xl border p-4 text-left transition-[border-color,background-color,transform] duration-300 active:scale-[0.98] ${
                      on ? 'border-[#C92F4B] bg-white shadow-[0_16px_40px_-20px_rgba(201,47,75,0.5)]' : 'border-[#E4E6EB] bg-white/60'
                    }`}
                  >
                    <span className={`flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-[0.12em] ${on ? 'text-[#A41225]' : 'text-[#1C1D21]'}`}>
                      <Icon size={17} /> {l.label}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.16em] ${l.operational ? 'text-[#15803D]' : 'text-[#646A72]'}`}>
                      {l.short}
                    </span>
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={layer.id}
                data-testid="layer-detail-panel"
                className="mt-6 rounded-2xl border border-[#E4E6EB] bg-white p-6 sm:p-7"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="font-display text-lg font-extrabold uppercase tracking-tight text-[#1C1D21]">{layer.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-[#404145]">{layer.text}</p>
                <span className={`mt-4 inline-block rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] ${
                  layer.operational ? 'bg-[#15803D]/10 text-[#15803D]' : 'bg-[#A7ABB0]/15 text-[#404145]'
                }`}>
                  {layer.status}
                </span>
              </motion.div>
            </AnimatePresence>

            <p className="mt-5 text-[11px] leading-relaxed text-[#646A72]">{CONNECTIVITY.disclaimer}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
